import { Injectable, OnDestroy } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { AuthRoles, AuthUserType } from '../auth/auth.types';
import { UserPreferenceType } from '../shared/app.enums';
import { AppPermissions } from '../shared/app.permissions';
import { AppUtils } from '../shared/app.utils';
import { DateRangeFilterModel } from '../shared/models/date-range-filter-model';
import { GeographicFilterModel } from '../shared/models/geographic-filter-model';
import { GeographicFilterRegionModel } from '../shared/models/geographic-filter-region-model';
import { AgencyService } from './agency.service';
import { LoginService } from './login.service';
import { LookupService } from './lookup.service';

@Injectable({
  providedIn: 'root'
})
export class FilterService implements OnDestroy {
  private _destroyed$: Subject<void> = new Subject<void>();
  private _geographicFilterModel: GeographicFilterModel = null;
  private _appUserPreferences: ServerModels.ENESS_ApplicationUserPreferences = null;
  private _userInfo: ServerModels.UserInfoViewModel = null;
  private _geoFilterStorageKeys: string[] = ["geoFilterStorageKeyLocations", "geoFilterStorageKeyCrimeGuns", "geoFilterStorageKeyPeople", "geoFilterStorageKeyEvents"];
  private _ballisticEvListFilterStorageKey: string = "ballisticEvidenceListFilter";

  private allAgenciesSubscription: Subscription = null;
  public allAgencies$: BehaviorSubject<ServerModels.ENESS_REF_Agency[]> = new BehaviorSubject([]);
  public agencies$: BehaviorSubject<ServerModels.ENESS_REF_Agency[]> = new BehaviorSubject<ServerModels.ENESS_REF_Agency[]>([]);
  public counties$: BehaviorSubject<ServerModels.ENESS_REF_Counties[]> = new BehaviorSubject<ServerModels.ENESS_REF_Counties[]>([]);
  public counties: Observable<ServerModels.ENESS_REF_Counties[]> = this.counties$.asObservable();
  public states$: BehaviorSubject<ServerModels.ENESS_REF_State[]> = new BehaviorSubject<ServerModels.ENESS_REF_State[]>([]);
  public fieldDivisionGroups$: BehaviorSubject<ServerModels.FieldDivisionGroupViewModel[]> = new BehaviorSubject<ServerModels.FieldDivisionGroupViewModel[]>([]);
  public fieldDivisions$: BehaviorSubject<ServerModels.ENESS_REF_FieldDivisions[]> = new BehaviorSubject<ServerModels.ENESS_REF_FieldDivisions[]>([]);
  public fieldDivisionName$: BehaviorSubject<string> = new BehaviorSubject<string>('All');
  public groupName$: BehaviorSubject<string> = new BehaviorSubject<string>('All');
  public groupNameList$: BehaviorSubject<string> = new BehaviorSubject<string>('All');
  public stateName$: BehaviorSubject<string> = new BehaviorSubject<string>('All');
  public countyName$: BehaviorSubject<string> = new BehaviorSubject<string>('All');
  public countyNameList$: BehaviorSubject<string> = new BehaviorSubject<string>('All');
  public agencyNames$: BehaviorSubject<string> = new BehaviorSubject<string>('All');
  public agencyNameList$: BehaviorSubject<string> = new BehaviorSubject<string>('All');
  public geographicFilterModel$: BehaviorSubject<GeographicFilterModel> = new BehaviorSubject<GeographicFilterModel>(null);
  public geographicFilterModel: Observable<GeographicFilterModel> = this.geographicFilterModel$.asObservable();
  public dateRangeFilterModel$: BehaviorSubject<DateRangeFilterModel> = new BehaviorSubject<DateRangeFilterModel>(null);
  public dateRangeFilterModel: Observable<DateRangeFilterModel> = this.dateRangeFilterModel$.asObservable();
  public filterPreferences$: BehaviorSubject<GeographicFilterModel> = new BehaviorSubject<GeographicFilterModel>(null);
  public filterPreferences: Observable<GeographicFilterModel> = this.filterPreferences$.asObservable();
  public filterPreferenceTypeId: string = null;

  private _hasLinkedAgencies: boolean = false;
  get hasLinkedAgencies(): boolean {
    return this._hasLinkedAgencies;
  }

  constructor(private loginService: LoginService,
    private lookupService: LookupService,
    private agencyService: AgencyService,
    private authService: AuthService) {

    this.agencyService.agencies$.pipe(takeUntil(this._destroyed$)).subscribe(this.allAgencies$);

    this._hasLinkedAgencies = !this.authService.hasRole(['NESS Administrator']) && this.authService.hasUserType(AppPermissions.canViewLinkedAgencies) && this.authService.linkedOrgCodes?.length > 0;

    this._geographicFilterModel = new GeographicFilterModel('all', 'all', [], 'all', 'all', [], 'all', [], 'All', 'All', 'All', 'All', 'All', 'All', 'All', 'All');

    this.loginService.userPreferenceTypes$.pipe(takeUntil(this._destroyed$)).subscribe((types: ServerModels.ENESS_REF_UserPreferenceTypes[]) => {
      this.filterPreferenceTypeId = types.find(x => x.UserPreferenceTypeName === UserPreferenceType.DefaultFilterPreferences).UserPreferenceTypeID;
    });
    this.loginService.userInfoData$.pipe(takeUntil(this._destroyed$)).subscribe((info: ServerModels.UserInfoViewModel) => {
      this._userInfo = info;
      if (!!info) {
        if (info.Roles.findIndex(x => x.Name == <AuthRoles>'NESS Administrator') > -1 && this._geoFilterStorageKeys.indexOf('geoFilterStorageKeyManageAgency') == -1) {
          this._geoFilterStorageKeys = [...['geoFilterStorageKeyManageAgency'], ...this._geoFilterStorageKeys];
        }
        if (info.Roles.findIndex(x => x.Name == <AuthRoles>'NESS Administrator') > -1 || info.UserType.UserTypeName !== <AuthUserType>'StateLocalLE') {
          this._geographicFilterModel.fieldDivisionID = info.FieldDivisionID;
          this.initGeoFilters();
        } else {
          if (this.hasLinkedAgencies) {
            info.LinkedAgencies.sort((a, b) => {
              return (a.AgencyName > b.AgencyName) ? 1 : (a.AgencyName < b.AgencyName) ? -1 : 0;
            });
            this.agencies$.next([...info.LinkedAgencies]);
            this._geoFilterStorageKeys = ['linkedAgenciesStKeyCrimeGuns'];
            this._geographicFilterModel = new GeographicFilterModel(null, null, [], null, null, [], 'all', [], null, null, null, null, null, null, 'All', 'All');
            this.setSessionStorage();
            this.geographicFilterModel$.next(this._geographicFilterModel);
          }
        }
      }
    });
    if (!this.filterPreferenceTypeId) {
      this.loginService.getUserPreferenceTypes().pipe(takeUntil(this._destroyed$)).subscribe((types: ServerModels.ENESS_REF_UserPreferenceTypes[]) => this.loginService.nextUserPreferenceType(types));
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  public initGeoFilters(): void {
    this.createUserPreference();
    const fdGroupIds: string = this._geographicFilterModel.fieldDivisionGroups?.map(x => x.IntelligenceReportATFReferralGroupsID)?.join(';');
    const countyIds: string = this._geographicFilterModel.counties?.map(x => x.CountyID)?.join(';');
    this.getFieldDivisions();
    this.getFieldDivisionGroups(this._geographicFilterModel.fieldDivisionID);
    this.getStates(this._geographicFilterModel.fieldDivisionID, fdGroupIds);
    this.getCounties(this._geographicFilterModel.fieldDivisionID, fdGroupIds, this._geographicFilterModel.stateID);
    this.getAgencies(this._geographicFilterModel.fieldDivisionID, fdGroupIds, this._geographicFilterModel.stateID, countyIds);
    this.setSessionStorage();
    if (!this._appUserPreferences) {
      this._appUserPreferences = {
        ApplicationUserPreferenceID: null,
        UserId: this._userInfo.Id,
        UserPreferenceTypeID: this.filterPreferenceTypeId,
        UserPreferenceSettingsJson: JSON.stringify(this._geographicFilterModel),
        CreatedOn: null,
        CreatedBy: null,
        ModifiedOn: null,
        ModifiedBy: null,
      } as ServerModels.ENESS_ApplicationUserPreferences;
    }
    this.filterPreferences$.next(this._geographicFilterModel);
    this.geographicFilterModel$.next(this._geographicFilterModel);
  }

  private createUserPreference(): void {

    let fm = {
      _fieldDivisionID: AppUtils.userFieldDivisionID(),
      _fieldDivisionGroupID: 'all',
      _fieldDivisionGroups: [] as ServerModels.FieldDivisionGroupViewModel[],
      _stateID: 'all',
      _countyID: 'all',
      _counties: [] as ServerModels.ENESS_REF_Counties[],
      _agencyID: 'all',
      _agencies: [] as ServerModels.ENESS_REF_Agency[],
      _fieldDivisionName: 'All',
      _groupName: 'All',
      _groupNameList: 'All',
      _stateName: 'All',
      _countyName: 'All',
      _countyNameList: 'All',
      _agencyName: 'All',
      _agencyNamesList: 'All'
    };

    this._userInfo.UserPreferences?.forEach(x => {
      if (!!x.ENESS_REF_UserPreferenceTypes && x.ENESS_REF_UserPreferenceTypes.UserPreferenceTypeName === UserPreferenceType.DefaultFilterPreferences) {
        this._appUserPreferences = x;
        const settingsJson = JSON.parse(x.UserPreferenceSettingsJson);
        fm = {
          _fieldDivisionID: settingsJson['_fieldDivisionID'] || 'all',
          _fieldDivisionGroupID: settingsJson['_fieldDivisionGroupID'] || 'all',
          _fieldDivisionGroups: settingsJson['_fieldDivisionGroups'] || [],
          _stateID: settingsJson['_stateID'] || 'all',
          _countyID: settingsJson['_countyID'] || 'all',
          _counties: settingsJson['_counties'] || [],
          _agencyID: settingsJson['_agencyID'] || 'all',
          _agencies: settingsJson['_agencies'] || [],
          _fieldDivisionName: settingsJson['_fieldDivisionName'] || 'All',
          _groupName: settingsJson['_groupName'] || 'All',
          _groupNameList: settingsJson['_groupNameList'] || 'All',
          _stateName: settingsJson['_stateName'] || 'All',
          _countyName: settingsJson['_countyName'] || 'All',
          _countyNameList: settingsJson['_countyNameList'] || 'All',
          _agencyName: settingsJson['_agencyName'] || 'All',
          _agencyNamesList: settingsJson['_agencyNamesList'] || 'All'
        };
      }
    });

    if (fm && Object.keys(fm).length > 0) {
      this.fieldDivisionName$.next(fm['_fieldDivisionName']);
      this.groupName$.next(fm['_groupName']);
      this.groupNameList$.next(fm['_groupNameList']);
      this.stateName$.next(fm['_stateName']);
      this.countyName$.next(fm['_countyName']);
      this.countyNameList$.next(fm['_countyNameList']);
      this.agencyNames$.next(fm['_agencyName']);
      this.agencyNameList$.next(fm['_agencyNamesList']);
      this._geographicFilterModel = new GeographicFilterModel(fm['_fieldDivisionID'], fm['_fieldDivisionGroupID'], fm['_fieldDivisionGroups'], fm['_stateID'], fm['_countyID'], fm['_counties'], fm['_agencyID'], fm['_agencies'], fm['_fieldDivisionName'], fm['_groupName'], fm['_groupNameList'], fm['_stateName'], fm['_countyName'], fm['_countyNameList'], fm['_agencyName'], fm['_agencyNamesList']);
    }
  }

  public getFieldDivisions(): void {
    this.fieldDivisions$.next(LoginService.fieldDivisionsSubj.getValue());
    this.setFieldDivisionName();
  }

  private setFieldDivisionName(): void {
    if (!!this._geographicFilterModel.fieldDivisionID && this._geographicFilterModel.fieldDivisionID.toLowerCase() === 'all') {
      this._geographicFilterModel.fieldDivisionName = 'All';
      this.fieldDivisionName$.next('All');
    } else {
      this.fieldDivisions$.getValue().forEach((x: ServerModels.ENESS_REF_FieldDivisions) => {
        if (!!x.FieldDivisionID && x.FieldDivisionID === this._geographicFilterModel.fieldDivisionID) {
          this._geographicFilterModel.fieldDivisionName = x.FieldDivisionName;
          this.fieldDivisionName$.next(x.FieldDivisionName);
        }
      });
    }
  }

  public getFieldDivisionGroups(fieldDivisionId: string): void {
    if (!fieldDivisionId || fieldDivisionId.toLowerCase() === 'all') {
      this.lookupService.fdGroups$.subscribe((allGroups: ServerModels.FieldDivisionGroupViewModel[]) => {
        this.fieldDivisionGroups$.next(allGroups);
        this.setFieldDivisionGroupNames();
      });
    } else {
      this.getFieldDivisionGroupsByFieldDivision(fieldDivisionId);
    }
  }

  private getFieldDivisionGroupsByFieldDivision(fieldDivisionId: string): void {
    if (!fieldDivisionId) {
      this.fieldDivisionGroups$.next([]);
      this.groupName$.next(null);
    } else {
      this.lookupService.getFDGroupByFieldDivision(fieldDivisionId).pipe(takeUntil(this._destroyed$)).subscribe((groups: ServerModels.FieldDivisionGroupViewModel[]) => {
        this.fieldDivisionGroups$.next(groups);
        this.setFieldDivisionGroupNames();
      });
    }
  }

  private setFieldDivisionGroupNames(): void {
    this.groupNameList$.next(this._geographicFilterModel.groupNameList);
    this.groupName$.next(this._geographicFilterModel.groupNameList);
  }

  public getStates(fieldDivisionId: string, fdGroups: string): void {
    if (!fieldDivisionId && !fdGroups) {
      this.states$.next([]);
      this.stateName$.next(null);
    } else if ((!fieldDivisionId || fieldDivisionId.toLowerCase() === 'all') && (!fdGroups || fdGroups?.toLowerCase() === 'all')) {
      this.lookupService.usStates$.subscribe((states: ServerModels.ENESS_REF_State[]) => {
        this.states$.next(states);
        this.setStateName();
      });
    } else {
      if (!!fdGroups && fdGroups?.toLowerCase() !== 'all') {
        this.getStatesForFieldDivisionGroups(fdGroups);
      } else {
        this.getStatesByFieldDivision(fieldDivisionId);
      }
    }
  }

  private setStateName(): void {
    if (this._geographicFilterModel.stateID.toLowerCase() === 'all') {
      this._geographicFilterModel.stateName = 'All';
      this.stateName$.next('All');
    } else {
      this.states$.getValue().forEach((x: ServerModels.ENESS_REF_State) => {
        if (x.StateID === this._geographicFilterModel.stateID) {
          this._geographicFilterModel.stateName = x.StateName;
          this.stateName$.next(x.StateName);
        }
      });
    }
  }

  private getStatesByFieldDivision(fieldDivisionId: string): void {
    if (!fieldDivisionId) {
      this.states$.next([]);
      this.stateName$.next(null);
    } else {
      this.lookupService.getStatesByFieldDivision(fieldDivisionId).pipe(takeUntil(this._destroyed$)).subscribe((states: ServerModels.ENESS_REF_State[]) => {
        this.states$.next(states);
        this.setStateName();
      });
    }
  }


  private getStatesForFieldDivisionGroups(fdGroups: string): void {
    if (!!fdGroups) {
      this.lookupService.getStatesForFieldDivisionGroups(fdGroups).pipe(takeUntil(this._destroyed$)).subscribe((groups: ServerModels.AgencyFieldDivisionGroupViewModel[]) => {
        if (groups) {
          let stateId = [...new Set(groups.map(x => x.StateID))];
          this.lookupService.usStates$.subscribe((states: ServerModels.ENESS_REF_State[]) => {
            let _states = states.filter(x => stateId.some(y => y == x.StateID))
            this.states$.next(_states);
            this.setStateName();
          });
        }
      })
    } else {
      this.states$.next([]);
      this.stateName$.next(null);
    }
  }

  public getCounties(fieldDivisionId: string, fdGroups: string, stateId: string): void {
    if (!fieldDivisionId && !fdGroups && !stateId) {
      this.counties$.next([]);
      this.countyName$.next(null);
      this.countyNameList$.next(null);
    } else if ((!fieldDivisionId || fieldDivisionId.toLowerCase() === 'all') && (!fdGroups || fdGroups.toLowerCase() === 'all') && (!stateId || stateId.toLowerCase() === 'all')) {
      this.getAllCounties();
    } else if (fieldDivisionId?.toLowerCase() !== 'all' && (!fdGroups || fdGroups?.toLowerCase() === 'all') && (!stateId || stateId?.toLowerCase() === 'all')) {
      this.getCountiesByFieldDivision(fieldDivisionId);
    } else if (!!fdGroups && fdGroups?.toLowerCase() !== 'all' && (!stateId || stateId?.toLowerCase() === 'all')) {
      this.getCountiesByFieldDivisionGroup(fdGroups);
    } else if (!!fdGroups && fdGroups?.toLowerCase() !== 'all' && (!stateId || stateId?.toLowerCase() !== 'all')) {
      this.getCountiesByStateAndFDGroup(fdGroups, stateId);
    } else if ((!fdGroups || fdGroups?.toLowerCase() === 'all') && (!!stateId && stateId.toLowerCase() !== 'all')) {
      this.getCountiesByState(stateId);
    }
  }

  private setCountyNames(): void {
    this.countyNameList$.next(this._geographicFilterModel.countyNameList);
    this.countyName$.next(this._geographicFilterModel.countyName);
  }

  private getAllCounties(): void {
    this.lookupService.counties$.pipe(takeUntil(this._destroyed$)).subscribe((allCounties: ServerModels.ENESS_REF_Counties[]) => {
      this.counties$.next(allCounties);
      this.setCountyNames();
    });
  }

  private getCountiesByFieldDivision(fieldDivisionId: string): void {
    if (!fieldDivisionId) {
      this.counties$.next([]);
      this.countyName$.next(null);
      this.countyNameList$.next(null);
    } else {
      this.lookupService.getCountiesByFieldDivision(fieldDivisionId).pipe(takeUntil(this._destroyed$)).subscribe((counties: ServerModels.ENESS_REF_Counties[]) => {
        this.counties$.next(counties);
        this.setCountyNames();
      });
    }
  }

  private getCountiesByFieldDivisionGroup(fieldDivisionGroupIds: string): void {
    if (!fieldDivisionGroupIds) {
      this.counties$.next([]);
      this.countyName$.next(null);
      this.countyNameList$.next(null);
    } else {
      this.lookupService.getCountiesForFieldDivisionGroup(fieldDivisionGroupIds).pipe(takeUntil(this._destroyed$)).subscribe((counties: ServerModels.ENESS_REF_Counties[]) => {
        this.counties$.next(counties);
        this.setCountyNames();
      });
    }
  }

  private getCountiesByStateAndFDGroup(fdGroups: string, stateId: string): void {
    if (!stateId && !fdGroups) {
      this.counties$.next([]);
      this.countyName$.next(null);
      this.countyNameList$.next(null);
    } else {
      this.lookupService.getCountiesForFDGroupAndState(fdGroups, stateId).subscribe((counties: ServerModels.ENESS_REF_Counties[]) => {
        this.counties$.next(counties);
        this.setCountyNames();
      });
    }
  }

  private getCountiesByState(stateId: string): void {
    if (!stateId) {
      this.counties$.next([]);
      this.countyName$.next(null);
      this.countyNameList$.next(null);
    } else {
      this.lookupService.getCountiesByState(stateId).subscribe((counties: ServerModels.ENESS_REF_Counties[]) => {
        this.counties$.next(counties);
        this.setCountyNames();
      });
    }
  }

  public getAgencies(fieldDivisionId: string, fgGroupIds: string, stateId: string, countyIds: string): void {
    if (!fieldDivisionId && !fgGroupIds && !stateId && !countyIds) {
      this.agencies$.next([]);
      this.agencyNameList$.next(null);
      this.agencyNames$.next(null);
    } else if (!!countyIds && countyIds?.toLowerCase() !== 'all' && !!fgGroupIds && fgGroupIds?.toLowerCase() !== 'all' && !!stateId && stateId?.toLowerCase() !== 'all') {
      this.getAgenciesByFDGroupStateCounty(fgGroupIds, countyIds, stateId);
    } else if (!!countyIds && countyIds?.toLowerCase() !== 'all' && !!fgGroupIds && fgGroupIds?.toLowerCase() !== 'all') {
      this.getAgenciesByFDGroupStateCounty(fgGroupIds, countyIds, 'all');
    } else if (!!fgGroupIds && fgGroupIds?.toLowerCase() !== 'all' && !!stateId && stateId?.toLowerCase() !== 'all') {
      this.getAgenciesByFDGroupStateCounty(fgGroupIds, '', stateId);
    } else if (!!countyIds && countyIds?.toLowerCase() !== 'all') {
      this.getAgenciesByCountyIds(countyIds);
    } else if (stateId?.toLowerCase() !== 'all') {
      this.getAgenciesByStateId(stateId);
    } else if (!!fgGroupIds && fgGroupIds?.toLowerCase() !== 'all') {
      this.getAgenciesByFieldDivisionGroup(fgGroupIds);
    } else if (!!fieldDivisionId && fieldDivisionId?.toLowerCase() !== 'all') {
      this.getAgenciesByFieldDivisionId(fieldDivisionId);
    } else {
      if (this.allAgenciesSubscription && !this.allAgenciesSubscription?.closed) {
        this.allAgenciesSubscription.unsubscribe();
      }
      this.allAgenciesSubscription = this.allAgencies$.pipe(takeUntil(this._destroyed$)).subscribe((val: ServerModels.ENESS_REF_Agency[]) => {
        this.agencies$.next(val);
        this.setAgencyNames();
      });
    }
  }

  private setAgencyNames(): void {
      this.agencyNameList$.next(this._geographicFilterModel.agencyNamesList);
      this.agencyNames$.next(this._geographicFilterModel.agencyName);
  }

  private getAgenciesByFieldDivisionId(fieldDivisionId: string): void {
    if (!fieldDivisionId) {
      this.agencies$.next([]);
      this.agencyNameList$.next(null);
      this.agencyNames$.next(null);
    }
    this.lookupService.getAgenciesByFieldDivisionId(fieldDivisionId).pipe(takeUntil(this._destroyed$)).subscribe((agencies: ServerModels.AgencyViewModel[]) => {
      this.agencies$.next(<ServerModels.ENESS_REF_Agency[]>agencies);
      this.setAgencyNames();
    });
  }

  private getAgenciesByFDGroupStateCounty(fdGroups: string, countyIds: string, stateId: string): void {
    if (!fdGroups && !countyIds && !stateId) {
      this.agencies$.next([]);
      this.agencyNameList$.next(null);
      this.agencyNames$.next(null);
    }
    this.lookupService.getAgenciesByFDGroupStateCounty(fdGroups, stateId, countyIds).pipe(takeUntil(this._destroyed$)).subscribe((agencies: ServerModels.AgencyViewModel[]) => {
      this.agencies$.next(<ServerModels.ENESS_REF_Agency[]>agencies);
      this.setAgencyNames();
    });
  }

  private getAgenciesByFieldDivisionGroup(fieldDivisionGroupIds: string): void {
    if (!fieldDivisionGroupIds) {
      this.agencies$.next([]);
      this.agencyNameList$.next(null);
      this.agencyNames$.next(null);
    }
    this.lookupService.getAgenciesByFieldDivisionGroupIds(fieldDivisionGroupIds).pipe(takeUntil(this._destroyed$)).subscribe((agencies: ServerModels.AgencyViewModel[]) => {
      this.agencies$.next(<ServerModels.ENESS_REF_Agency[]>agencies);
      this.setAgencyNames();
    });
  }

  private getAgenciesByStateId(stateId: string): void {
    if (!stateId) {
      this.agencies$.next([]);
      this.agencyNameList$.next(null);
      this.agencyNames$.next(null);
    }
    this.agencyService.getAgenciesForState(stateId).pipe(takeUntil(this._destroyed$)).subscribe((agencies: ServerModels.ENESS_REF_Agency[]) => {
      this.agencies$.next(agencies);
      this.setAgencyNames();
    });
  }

  private getAgenciesByCountyIds(countyIds: string): void {
    if (!countyIds) {
      this.agencies$.next([]);
      this.agencyNameList$.next(null);
      this.agencyNames$.next(null);
    } else {
      this.lookupService.getAgenciesByCountyIds(countyIds).pipe(takeUntil(this._destroyed$)).subscribe((agencies: ServerModels.AgencyViewModel[]) => {
        this.agencies$.next(<ServerModels.ENESS_REF_Agency[]>agencies);
        this.setAgencyNames();
      });
    }
  }

  private setSessionStorage(): void {
    for (let i = 0; i < this._geoFilterStorageKeys.length; i++) {
      let dm = JSON.parse(sessionStorage.getItem(this._geoFilterStorageKeys[i]));
      if (!!dm && Object.keys(dm).length > 0) {
        return;
      }
      else {
        sessionStorage.setItem(this._geoFilterStorageKeys[i], JSON.stringify(this._geographicFilterModel));
      }
    }
  }

  public applyDateFilters(storageKey: string, duration: number): void {
    let dm = JSON.parse(sessionStorage.getItem(storageKey));
    if (!!dm && Object.keys(dm).length > 0) {
      dm['_endDate'] = new Date(dm['_endDate']);
      dm['_startDate'] = new Date(dm['_startDate']);
    } else {
      let dateRange = this.getDateRange(duration);
      dm = {
        _endDate: dateRange.endDate,
        _startDate: dateRange.startDate,
        _fieldDivisionID: AppUtils.userFieldDivisionID(),
        _cgicCode: AppUtils.userCGICCode(),
        _duration: duration
      }
    }
    this.dateRangeFilterModel$.next(new DateRangeFilterModel(dm['_fieldDivisionID'] || AppUtils.userFieldDivisionID(), dm['_duration'] || duration, dm['_startDate'], dm['_endDate']));
  }

  public applyGeoFilters(storageKey: string): void {
    let fm = JSON.parse(sessionStorage.getItem(storageKey));
    if (!!fm && Object.keys(fm).length > 0) {
      this._geographicFilterModel = new GeographicFilterModel(fm['_fieldDivisionID'], fm['_fieldDivisionGroupID'], fm['_fieldDivisionGroups'], fm['_stateID'], fm['_countyID'], fm['_counties'], fm['_agencyID'], fm['_agencies'], fm['_fieldDivisionName'], fm['_groupName'], fm['_groupNameList'], fm['_stateName'], fm['_countyName'], fm['_countyNameList'], fm['_agencyName'], fm['_agencyNamesList']);

      if (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(['ATFFieldDivision', 'ATFHQ'])) {
        const fdGroupIds: string = this._geographicFilterModel.fieldDivisionGroups.map(x => x.IntelligenceReportATFReferralGroupsID)?.join(';');
        const countyIds: string = this._geographicFilterModel.counties.map(x => x.CountyID)?.join(';');
        this.getFieldDivisions();
        this.getFieldDivisionGroups(fm['_fieldDivisionID']);
        this.getStates(fm['_fieldDivisionID'], fdGroupIds);
        this.getCounties(fm['_fieldDivisionID'], fdGroupIds, fm['_stateID']);
        this.getAgencies(fm['_fieldDivisionID'], fdGroupIds, fm['_stateID'], countyIds);
      }
      this.geographicFilterModel$.next(this._geographicFilterModel);
    }
  }

  public resetGeoFilterDefaults(storageKey: string = null): void {
    let defaultFilterModel: GeographicFilterModel = null;
    if (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(['ATFFieldDivision', 'ATFHQ'])) {
      defaultFilterModel = <GeographicFilterModel>JSON.parse(this._appUserPreferences.UserPreferenceSettingsJson);
      const fdGroupIds: string = defaultFilterModel['_fieldDivisionGroups'].map(x => x.IntelligenceReportATFReferralGroupsID)?.join(';');
      const countyIds: string = defaultFilterModel['_counties'].map(x => x.CountyID)?.join(';');
      this.getFieldDivisions();
      this.getFieldDivisionGroups(defaultFilterModel['_fieldDivisionID']);
      this.getStates(defaultFilterModel['_fieldDivisionID'], fdGroupIds);
      this.getCounties(defaultFilterModel['_fieldDivisionID'], fdGroupIds, defaultFilterModel['_stateID']);
      this.getAgencies(defaultFilterModel['_fieldDivisionID'], fdGroupIds, defaultFilterModel['_stateID'], countyIds);
    } else if (!!storageKey) {
      defaultFilterModel = <GeographicFilterModel>JSON.parse(sessionStorage.getItem(storageKey));
    }

    if (defaultFilterModel) {
      this._geographicFilterModel = new GeographicFilterModel(defaultFilterModel['_fieldDivisionID'], defaultFilterModel['_fieldDivisionGroupID'], defaultFilterModel['_fieldDivisionGroups'], defaultFilterModel['_stateID'], defaultFilterModel['_countyID'], defaultFilterModel['_counties'], defaultFilterModel['_agencyID'], defaultFilterModel['_agencies'], defaultFilterModel['_fieldDivisionName'], defaultFilterModel['_groupName'], defaultFilterModel['_groupNameList'], defaultFilterModel['_stateName'], defaultFilterModel['_countyName'], defaultFilterModel['_countyNameList'], defaultFilterModel['_agencyName'], defaultFilterModel['_agencyNamesList']);
      this.geographicFilterModel$.next(this._geographicFilterModel);
    }
  }

  public resetDatesFilterDefaults(duration: number): void {
    let dateRange = this.getDateRange(duration);
    this.dateRangeFilterModel$.next(new DateRangeFilterModel(AppUtils.userFieldDivisionID(), duration, dateRange.startDate, dateRange.endDate));
  }

  private getDateRange(duration: number) {
    let momentDuration: moment.Duration = moment.duration(duration, 'days');
    let end: Date = new Date(new Date().setHours(23, 59, 59, 999));
    return { endDate: end, startDate: moment(end).startOf('day').subtract(momentDuration).toDate() };
  }

  public getUserInfo(): ServerModels.UserInfoViewModel {
    return this._userInfo;
  }

  public saveFilterPreferences(fm: GeographicFilterModel): void {
    this._geoFilterStorageKeys.forEach(geoFilterStorageKey => sessionStorage.setItem(geoFilterStorageKey, JSON.stringify(fm)));

    let agenciesVM: ServerModels.AgencyViewModel[] = fm['_agencies']?.map(a => <ServerModels.AgencyViewModel>{
      AgencyID: a.AgencyID,
      AgencyName: a.AgencyName,
      AgencyAbbreviation: a.AgencyAbbreviation,
      CountyID: a.CountyID,
      ENESS_REF_Organization_ID: a.ENESS_REF_Organization_ID,
      FieldDivisionID: a.FieldDivisionID,
      ENESS_REF_Counties: a.ENESS_REF_Counties,
      ENESS_REF_FieldDivisions: a.ENESS_REF_FieldDivisions,
      ENESS_XREF_AgencyOriNum: a.ENESS_XREF_AgencyOriNum
    }) || [];
    const geoRegionFilterModel: GeographicFilterRegionModel = new GeographicFilterRegionModel(fm['_fieldDivisionID'], fm['_stateID'], fm['_countyID'], fm['_counties'], fm['_fieldDivisionName'], fm['_stateName'], fm['_countyName'], fm['_countyNameList'], fm['_agencyID'], fm['_agencyName'], fm['_agencyNamesList'], agenciesVM, fm['_fieldDivisionGroupID'], fm['_groupName'], fm['_groupNameList'], fm['_fieldDivisionGroups']);
    sessionStorage.setItem(this._ballisticEvListFilterStorageKey, JSON.stringify(geoRegionFilterModel));

    if (this._appUserPreferences && !!Object.keys(this._appUserPreferences).length) {
      this._appUserPreferences.UserPreferenceSettingsJson = JSON.stringify(fm);
      this._appUserPreferences.UserPreferenceTypeID = !this._appUserPreferences.UserPreferenceTypeID ? this.filterPreferenceTypeId : this._appUserPreferences.UserPreferenceTypeID;
    } else {
      this._appUserPreferences = {
        ApplicationUserPreferenceID: null,
        UserId: this._userInfo.Id,
        UserPreferenceTypeID: this.filterPreferenceTypeId,
        UserPreferenceSettingsJson: JSON.stringify(fm),
        CreatedOn: new Date()
      } as ServerModels.ENESS_ApplicationUserPreferences;
    }

    this.loginService.setUserPreference(this._appUserPreferences).pipe(takeUntil(this._destroyed$)).subscribe((pref: ServerModels.ENESS_ApplicationUserPreferences) => {
      this._appUserPreferences = pref;
      if (!this._userInfo.UserPreferences.length) {
        this._userInfo.UserPreferences = [pref];
      } else {
        this._userInfo.UserPreferences.forEach(x => {
          if (x.ApplicationUserPreferenceID === pref.ApplicationUserPreferenceID) {
            x = pref;
          }
        });
      }
      this.loginService.nextUserInfo(this._userInfo);
    });

  }

  public optionDisabled(option: any): boolean {
    if (!!option && option.hasOwnProperty('Inactive')) {
      return !!option['Inactive'];
    } else {
      return false;
    }
  }
}
